// Pure reframe logic for the AOE2 curation editor.
//
// This module MUST stay numerically identical to the template's
// `reframeAt` function and its clamp block in
// remotion-service/templates/src/aoe2-replay-short/Aoe2ReplayShort.tsx.
// Any change to the template math must be mirrored here and vice-versa —
// the test suite pins parity via matching inputs/outputs.
//
// No DOM, no Stimulus.

// Target composition dimensions (1080×1920 = 9:16 vertical).
// Mirrors `useVideoConfig()` returning width=1080, height=1920 in the template.
const COMPOSITION_WIDTH = 1080
const COMPOSITION_HEIGHT = 1920

// Piecewise-linear interpolation with clamp extrapolation.
// Mirrors Remotion's `interpolate(value, inputRange, outputRange, {extrapolateLeft:'clamp', extrapolateRight:'clamp'})`.
//
// inputRange must be sorted ascending and have the same length as outputRange.
function linearInterpolate(value, inputRange, outputRange) {
  if (inputRange.length === 0) return 0

  // Clamp left
  if (value <= inputRange[0]) return outputRange[0]

  // Clamp right
  const last = inputRange.length - 1
  if (value >= inputRange[last]) return outputRange[last]

  // Find the segment
  let i = 0
  while (i < last - 1 && inputRange[i + 1] <= value) {
    i++
  }

  const t = (value - inputRange[i]) / (inputRange[i + 1] - inputRange[i])
  return outputRange[i] + t * (outputRange[i + 1] - outputRange[i])
}

// Interpolate the crop rect at a given source frame.
//
// Mirrors `reframeAt(frame, kfs)` in Aoe2ReplayShort.tsx exactly:
//   - 0 or 1 keyframe → return that keyframe (or default)
//   - 2+ keyframes → piecewise-linear interpolation with clamp on all three axes
//
// `frame` is the SOURCE frame (compositionFrame + trimStartFrames in the template).
// `keyframes` is an array of { frame, x, y, w } objects, sorted ascending by frame.
//
// Returns { x, y, w } — the crop origin and width in source pixels.
export function interpolateReframe(frame, keyframes) {
  if (!keyframes || keyframes.length === 0) {
    return { x: 0, y: 0, w: 1920 }
  }
  if (keyframes.length === 1) {
    return { x: keyframes[0].x, y: keyframes[0].y, w: keyframes[0].w }
  }

  const sorted = sortByFrame(keyframes)
  const frames = sorted.map((k) => k.frame)
  return {
    x: linearInterpolate(frame, frames, sorted.map((k) => k.x)),
    y: linearInterpolate(frame, frames, sorted.map((k) => k.y)),
    w: linearInterpolate(frame, frames, sorted.map((k) => k.w)),
  }
}

// Compute crop height from crop width, preserving the 9:16 output aspect ratio.
// Mirrors: `const cropH = (w * height) / width` where height=1920, width=1080.
export function cropHeight(w) {
  return (w * COMPOSITION_HEIGHT) / COMPOSITION_WIDTH
}

// Clamp a crop rect so it stays inside the source video and the play area.
//
// Mirrors the clamp block in Aoe2ReplayShort.tsx:
//   x = Math.max(0, Math.min(sourceWidth - w, rf.x))
//   y = Math.max(playTop, Math.min(playBottom - cropH, rf.y))
//
// Inputs:
//   rect        — { x, y, w } in source pixels
//   constraints — { sourceWidth, playTop, playBottom }
//
// Returns a new { x, y, w } with clamped coordinates (w unchanged).
export function clampCrop(rect, { sourceWidth, playTop, playBottom }) {
  const { x, y, w } = rect
  const cropH = cropHeight(w)
  return {
    w,
    x: Math.max(0, Math.min(sourceWidth - w, x)),
    y: Math.max(playTop, Math.min(playBottom - cropH, y)),
  }
}

// The maximum crop width that fits within the play area height.
// Solving cropHeight(w) <= (playBottom - playTop) for w:
//   w * 1920/1080 <= (playBottom - playTop)
//   w <= (playBottom - playTop) * 1080/1920
export function maxWidthForPlayArea(playTop, playBottom) {
  return ((playBottom - playTop) * COMPOSITION_WIDTH) / COMPOSITION_HEIGHT
}

// Replace the keyframe at the given frame (if any) or insert a new one.
// Returns a new sorted array — does not mutate the input.
export function upsertKeyframe(keyframes, keyframe) {
  const existing = keyframes.filter((k) => k.frame !== keyframe.frame)
  return sortByFrame([...existing, keyframe])
}

// Remove the keyframe at the given frame (if any).
// Returns a new array — does not mutate the input.
export function removeKeyframe(keyframes, frame) {
  return keyframes.filter((k) => k.frame !== frame)
}

// Sort keyframes ascending by frame. Returns a new array.
export function sortByFrame(keyframes) {
  return [...keyframes].sort((a, b) => a.frame - b.frame)
}
